import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { useManagement } from "../context/ManagementContext";
import { db } from "../config/fbConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import "../styles/Home.css";
import Loader from "./Loader";
import CurrentTermModal from "./CurrentTermModal";

const CurrentClasses = () => {
  const { user, userData, loading: authLoading } = useUserAuth();
  const {
    managementData,
    setManagementData,
    loading,
    setLoading,
    showTermModal,
    setShowTermModal,
  } = useManagement();
  const navigate = useNavigate();
  const classListRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Restore scroll position when component mounts
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('managementScrollPosition');
    if (savedScrollPosition && classListRef.current) {
      setTimeout(() => {
        classListRef.current.scrollTop = parseInt(savedScrollPosition, 10);
      }, 0);
    }

    // Clean up
    return () => {
      sessionStorage.removeItem('managementScrollPosition');
    };
  }, []);

  // Save scroll position when navigating away
  useEffect(() => {
    const saveScrollPosition = () => {
      if (classListRef.current) {
        sessionStorage.setItem('managementScrollPosition', classListRef.current.scrollTop);
      }
    };

    window.addEventListener('beforeunload', saveScrollPosition);
    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      console.log("fetchData started", { user: !!user, userData: !!userData, authLoading });
      setLoading(true);

      try {
        const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
        if (!currentTermDoc.exists()) {
          console.error("Current term document does not exist");
          setManagementData(prev => ({
            ...prev,
            parentKids: [],
            lastFetched: new Date().toISOString(),
          }));
          setLoading(false);
          return;
        }
        const currentTermData = currentTermDoc.data();
        const currentSessionId = currentTermData?.sessionId;
        const currentTermId = currentTermData?.termId;

        if (!currentSessionId || !currentTermId) {
          console.error("Current session or term not found", { currentSessionId, currentTermId });
          setManagementData(prev => ({
            ...prev,
            parentKids: [],
            lastFetched: new Date().toISOString(),
          }));
          setLoading(false);
          return;
        }

        // Check if data is still valid (fetched within last 5 minutes)
        const now = new Date();
        const lastFetched = managementData.lastFetched ? new Date(managementData.lastFetched) : null;
        const isDataStale = !lastFetched || (now - lastFetched) > 5 * 60 * 1000;
        const termChanged =
          managementData.currentTerm?.sessionId !== currentSessionId ||
          managementData.currentTerm?.termId !== currentTermId;

        if (!isDataStale && !termChanged) {
          console.log("Using cached data");
          setLoading(false);
          return;
        }

        const sectionsQuery = query(collection(db, "sections"));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        const sectionsData = Object.fromEntries(
          sectionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        const sessionsQuery = query(
          collection(db, "sessions"),
          where("__name__", "==", currentSessionId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = Object.fromEntries(
          sessionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        const termsQuery = query(
          collection(db, "terms"),
          where("__name__", "==", currentTermId)
        );
        const termsSnapshot = await getDocs(termsQuery);
        const termsData = Object.fromEntries(
          termsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        if (userData?.status === "Parent" && userData?.isActive === true) {
          const kidIds = userData.kids || [];
          console.log("Fetching kids for Parent", { kidIds });
          let kidsData = [];

          if (kidIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < kidIds.length; i += 10) {
              chunks.push(kidIds.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              const studentsQuery = query(
                collection(db, "students"),
                where("__name__", "in", chunk)
              );
              const studentsSnapshot = await getDocs(studentsQuery);
              kidsData = [
                ...kidsData,
                ...studentsSnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const classesQuery = query(
            collection(db, "classes"),
            where("session", "==", currentSessionId),
            where("term", "==", currentTermId)
          );
          const classesSnapshot = await getDocs(classesQuery);
          const classMap = Object.fromEntries(
            classesSnapshot.docs.map((doc) => [
              doc.id,
              {
                name: doc.data().name || "Unknown Class",
                sectionId: doc.data().section || "",
                sessionId: doc.data().session || "",
                termId: doc.data().term || "",
              },
            ])
          );

          const kidsWithEnrollments = kidsData
            .flatMap((kid) => {
              const enrollments = kid.enrollments || [];
              const validEnrollments = enrollments
                .filter((e) => classMap[e.classId])
                .map((e) => {
                  const classId = e.classId;
                  const classInfo = classMap[classId];
                  return {
                    kidId: kid.id,
                    kidName: `${kid.firstName} ${kid.lastName}`.trim() || "Unknown Kid",
                    classId,
                    className: classInfo.name,
                    sectionId: classInfo.sectionId,
                    sectionName: sectionsData[classInfo.sectionId] || "Unknown Section",
                    sessionId: currentSessionId,
                    sessionName: sessionsData[currentSessionId] || "Unknown Session",
                    termId: currentTermId,
                    termName: termsData[currentTermId] || "Unknown Term",
                  };
                });
              return validEnrollments.length > 0 ? validEnrollments : [];
            })
            .sort((a, b) => a.kidName.localeCompare(b.kidName));

          console.log("Kids data fetched", { kidsWithEnrollments });
          setManagementData(prev => ({
            ...prev,
            parentKids: kidsWithEnrollments,
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId,
            },
          }));
        } else if (userData?.status === "Teacher" && userData?.isActive === true) {
          const classesQuery = query(
            collection(db, "classes"),
            where("session", "==", currentSessionId),
            where("term", "==", currentTermId),
            where("formMaster", "==", user.uid)
          );
          const classesSnapshot = await getDocs(classesQuery);

          const formMasterClasses = classesSnapshot.docs.map(doc => {
            const classDoc = doc.data();
            return {
              id: doc.id,
              name: classDoc.name,
              sectionId: classDoc.section || "",
              sessionId: currentSessionId,
              sessionName: sessionsData[currentSessionId],
              termId: currentTermId,
              termName: termsData[currentTermId],
            };
          });

          const teachingAssignments = userData.teachingAssignments || [];
          const classIds = [...new Set(teachingAssignments.map((ta) => ta.classId))];

          let classesData = [];
          if (classIds.length > 0) {
            const classChunks = [];
            for (let i = 0; i < classIds.length; i += 30) {
              classChunks.push(classIds.slice(i, i + 30));
            }

            for (const chunk of classChunks) {
              const queryChunk = query(
                collection(db, "classes"),
                where("__name__", "in", chunk),
                where("session", "==", currentSessionId),
                where("term", "==", currentTermId)
              );
              const snapshot = await getDocs(queryChunk);
              classesData = [
                ...classesData,
                ...snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const subjectIds = [...new Set(teachingAssignments.map((ta) => ta.subjectId))];

          let subjectsData = [];
          if (subjectIds.length > 0) {
            const subjectChunks = [];
            for (let i = 0; i < subjectIds.length; i += 30) {
              subjectChunks.push(subjectIds.slice(i, i + 30));
            }

            for (const chunk of subjectChunks) {
              const queryChunk = query(
                collection(db, "subjects"),
                where("__name__", "in", chunk)
              );
              const snapshot = await getDocs(queryChunk);
              subjectsData = [
                ...subjectsData,
                ...snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const combinedData = teachingAssignments
            .filter((ta) => classesData.some((cls) => cls.id === ta.classId))
            .map((assignment) => {
              const classInfo = classesData.find((cls) => cls.id === assignment.classId);
              const subjectInfo = subjectsData.find((subj) => subj.id === assignment.subjectId);
              const subjectInClass = classInfo?.subjects?.find((subj) => subj.id === assignment.subjectId);
              return {
                classId: assignment.classId,
                className: classInfo?.name || "Unknown Class",
                subjectId: assignment.subjectId,
                subjectName: subjectInfo?.name || "Unknown Subject",
                pdfLink: subjectInClass?.pdfLink || "", // Include pdfLink from the class's subjects
                sectionName: sectionsData[classInfo?.section] || "Unknown Section",
                sessionId: classInfo?.session || "",
                sessionName: sessionsData[classInfo?.session] || "Unknown Session",
                termId: classInfo?.term || "",
                termName: termsData[classInfo?.term] || "Unknown Term",
              };
            });

          setManagementData(prev => ({
            ...prev,
            teacherSubjects: combinedData,
            formMasterClasses: formMasterClasses,
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId,
            },
          }));
        } else if (userData?.status === "Admin" || userData?.status === "Cashier") {
          const classesQuery = query(
            collection(db, "classes"),
            where("session", "==", currentSessionId),
            where("term", "==", currentTermId)
          );
          const classesSnapshot = await getDocs(classesQuery);
          const classesData = classesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            subjects: doc.data().subjects || [],
          }));

          const allSubjectIds = [
            ...new Set(
              classesData.flatMap((cls) =>
                cls.subjects.map((subj) => subj?.id).filter(Boolean)
              )
            ),
          ];
         
          let subjectsMap = {};
          if (allSubjectIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < allSubjectIds.length; i += 30) {
              chunks.push(allSubjectIds.slice(i, i + 30));
            }

            for (const chunk of chunks) {
              const snapshot = await getDocs(
                query(collection(db, "subjects"), where("__name__", "in", chunk))
              );
              snapshot.docs.forEach((doc) => {
                subjectsMap[doc.id] = doc.data().name;
              });
            }
          }

          const teachersQuery = query(
            collection(db, "users"),
            where("status", "in", ["Teacher", "Cashier", "Admin"]),
            where("isActive", "==", true)

          );
          const teachersSnapshot = await getDocs(teachersQuery);

          const teachersMap = Object.fromEntries(
            teachersSnapshot.docs.map((doc) => [
              doc.id,
              {
                name:
                  `${doc.data().firstName || ""} ${doc.data().lastName || ""}`.trim() ||
                  "No Teacher",
                firstName: doc.data().firstName || "",
                lastName: doc.data().lastName || "",
                teachingAssignments: doc.data().teachingAssignments || [],
              },
            ])
          );

          const assignmentsMap = {};
          teachersSnapshot.docs.forEach((doc) => {
            const teacher = doc.data();
            (teacher.teachingAssignments || []).forEach((ta) => {
              const key = `${ta.classId}_${ta.subjectId}`;
              if (!assignmentsMap[key]) {
                assignmentsMap[key] = [];
              }
              assignmentsMap[key].push({
                teacherId: doc.id,
                firstName: teacher.firstName || "",
                lastName: teacher.lastName || "",
                teacherName:
                  `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
                  "Unknown Teacher",
              });
            });
          });

          const groupedData = classesData.map((cls) => {
            const hasSubjects = cls.subjects && cls.subjects.length > 0;
            
            const subjectsForClass = hasSubjects
              ? cls.subjects
                  .map((subj) => {
                    if (!subj?.id) return null;

                    // Only include if the subject actually exists in subjectsMap
                    if (!subjectsMap[subj.id]) return null;

                    const assignmentKey = `${cls.id}_${subj.id}`;
                    const teachers = assignmentsMap[assignmentKey] || [];

                    return {
                      subjectId: subj.id,
                      subjectName: subjectsMap[subj.id], // Safe now — we checked it exists
                      pdfLink: subj.pdfLink || "",
                      teachers:
                        teachers.length > 0
                          ? teachers
                          : [{ teacherName: "No teacher" }],
                    };
                  })
                  .filter(Boolean) // Removes all null entries
              : null;

            return {
              classId: cls.id,
              className: cls.name || "Unknown Class",
              sessionId: cls.session,
              sectionId: cls.section,
              sectionName: sectionsData[cls.section] || "Unknown Section",
              sessionName: sessionsData[cls.session] || "Unknown Session",
              termId: cls.term,
              termName: termsData[cls.term] || "Unknown Term",
              formMaster: cls.formMaster,
              formMasterName: teachersMap[cls.formMaster]?.name || "No Class Teacher",
              subjects: subjectsForClass?.length ? subjectsForClass : null,
            };
          });

          setManagementData(prev => ({
            ...prev,
            adminClasses: groupedData,
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setManagementData(prev => ({
          ...prev,
          parentKids: [],
          lastFetched: new Date().toISOString(),
        }));
      } finally {
        setLoading(false);
        console.log("fetchData completed");
      }
    };

    if (user && userData && !authLoading) {
      fetchData();
    } else {
      console.log("fetchData skipped", { user: !!user, userData: !!userData, authLoading });
      setLoading(false);
    }
  }, [user, userData, authLoading]);

  // Filter classes based on search term and selected filter
  const filterClasses = (classes) => {
    if (!classes) return [];
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return classes.filter(cls => {
      // Filter by search term
      const matchesSearch = 
        cls.className.toLowerCase().includes(lowerSearchTerm) ||
        cls.sectionName.toLowerCase().includes(lowerSearchTerm) ||
        cls.sessionName.toLowerCase().includes(lowerSearchTerm) ||
        cls.termName.toLowerCase().includes(lowerSearchTerm) ||
        (cls.subjects && cls.subjects.some(subj => 
          subj.subjectName.toLowerCase().includes(lowerSearchTerm)
        ));

      return matchesSearch;
    });
  };

  if (loading || authLoading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <p className="no-subjects">User data not available</p>;
  }

  const { teacherSubjects, adminClasses, formMasterClasses, parentKids } = managementData;
  const filteredClasses = filterClasses(adminClasses);

  const saveScrollAndNavigate = (path) => {
    if (classListRef.current) {
      sessionStorage.setItem('managementScrollPosition', classListRef.current.scrollTop);
    }
    navigate(path);
  };

  return (
    <>
      <div className="dashboard-container padding-bottom-d">
        {userData?.status === "Parent" && userData?.isActive === true ? (
          <div className="parent-dashboard">
            <p className="size5 caps login-heading page-heading page-heading-d">
              My Kids' Academic Records
            </p>
            {parentKids.length > 0 ? (
              <div className="subject-list">
                {parentKids.map((kid, index) => (
                  <div key={index} className="subject-card-group categories-container">
                    <p className="text-center">
                        <strong className="bold male-female-stat">{kid.kidName}</strong><br />
                        <span className="size5">{kid.className} ({kid.sectionName} - {kid.sessionName} -{" "}
                            {kid.termName} Term)
                        </span>
                    </p>                    
                    <div
                      className="subject-card subject-card-my-kids"
                      onClick={() => saveScrollAndNavigate(
                        `/results/${kid.sectionId}/${kid.sessionId}/${kid.termId}/${kid.classId}/${kid.kidId}`
                      )}
                    >
                      <p className="bold male-female-stat">
                        Results
                      </p>
                    </div>
                    <div
                      className="subject-card subject-card-my-kids"
                      onClick={() => saveScrollAndNavigate(
                        `/attendance/${kid.classId}/${kid.kidId}?section=${kid.sectionId}&session=${kid.sessionId}&term=${kid.termId}&sessionName=${encodeURIComponent(
                          kid.sessionName
                        )}&termName=${encodeURIComponent(kid.termName)}`
                      )}
                    >
                      <p className="bold male-female-stat" style={{width: '100%'}}>
                        Attendance
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-subjects">No kids enrolled this term</p>
            )}
          </div>
        ) : userData?.status === "Admin" || userData?.status === "Cashier" ? (
          <>
            <p className="caps login-heading page-heading page-heading-d">
              CLASSES
            </p>            
            
            {/* Search and Filter Section */}
            <div className="management-search-bar">
              <input
                type="text"
                className="input-field1 management-search-input"
                placeholder="Search by class, section, subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}                  
              />             
            </div>

            <div className="class-list" ref={classListRef}>
              {filteredClasses.length > 0 ? (
                // First group classes by section
                Object.entries(
                  filteredClasses.reduce((acc, cls) => {
                    if (!acc[cls.sectionName]) {
                      acc[cls.sectionName] = [];
                    }
                    acc[cls.sectionName].push(cls);
                    return acc;
                  }, {})
                ).map(([sectionName, classesInSection]) => (
                  <>
                  <h3 className="page-heading page-heading-d">{sectionName} Section</h3>
                  <div className='all-categories' key={sectionName}>
                    {classesInSection.map((cls, index) => (
                      <div>
                        <div key={index} className="categories-container">
                          <Link to={`/classes/${cls.classId}`} className="login-heading page-heading italics page-heading-d class-button">
                            {cls.className} ({cls.sectionName} - {cls.sessionName} -{" "}
                            {cls.termName} Term)
                          </Link>
                          <div className="subject-cards-d">
                            <div
                              className="subject-card"
                              onClick={() => saveScrollAndNavigate(
                                `/archives/${cls.sectionId}/${cls.sessionId}/${cls.termId}/${cls.classId}`
                              )}
                            >
                              <p className="bold male-female-stat">
                                {cls.className} Full Result
                              </p>
                              <div className="teacher-list">
                                <p className="size5">
                                  {cls.sessionName} - {cls.termName} Term
                                </p>
                              </div>
                            </div>
                            <div
                              className="subject-card"
                              onClick={() => saveScrollAndNavigate(
                                `/attendance/${cls.classId}?section=${cls.sectionId}&session=${cls.sessionId}&term=${cls.termId}&sessionName=${encodeURIComponent(
                                  cls.sessionName
                                )}&termName=${encodeURIComponent(cls.termName)}`
                              )}
                            >
                              <p className="bold male-female-stat">
                                {cls.className} Attendance
                              </p>
                              <div className="teacher-list">
                                <p className="size5">{cls.formMasterName}</p>
                              </div>
                            </div>
                            <div
                              className="subject-card"
                              onClick={() => saveScrollAndNavigate(
                                `/psychomotor/${cls.classId}?session=${cls.sessionId}&term=${cls.termId}&sessionName=${encodeURIComponent(
                                  cls.sessionName
                                )}&termName=${encodeURIComponent(cls.termName)}`
                              )}
                            >
                              <p className="bold male-female-stat">
                                {cls.className} Psychomotor Skills
                              </p>
                              <div className="teacher-list">
                                <p className="size5">{cls.formMasterName}</p>
                              </div>
                            </div>
                          </div>

                          <div className="subject-list">
                            {cls.subjects ? (
                              cls.subjects?.map((item, subIndex) => (
                                <div
                                  key={subIndex}
                                  className="subject-card subject-card-management"
                                  onClick={() => saveScrollAndNavigate(
                                    `/scores/${cls.classId}/${item.subjectId}?session=${cls.sessionId}&term=${cls.termId}&sessionName=${encodeURIComponent(
                                      cls.sessionName
                                    )}&termName=${encodeURIComponent(cls.termName)}`
                                  )}
                                >
                                  <div>
                                    <p className="bold male-female-stat">
                                      {item.subjectName}
                                    </p>
                                    <div className="teacher-list">
                                      <p className="size5">
                                        {item.teachers
                                          .map((teacher) => teacher.teacherName)
                                          .join(", ")}
                                      </p>
                                    </div>
                                  </div>
                                  {item.pdfLink && (
                                    <button
                                      className="list-btn"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent card click from triggering
                                        navigate(`/pdf/${encodeURIComponent(item.pdfLink)}`);
                                      }}
                                    >
                                      <i className="fas fa-file-pdf"></i>
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : (
                              <p className="italics size5 bold">No Subject Registered</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                ))
              ) : (
                <p className="no-subjects">No classes found matching your search</p>
              )}
            </div>
          </>
        ) : userData?.status === "Teacher" ? (
          <div className="teacher-dashboard">        
            {formMasterClasses && formMasterClasses.length > 0 && (
              <>
                <p className="size5 caps login-heading page-heading page-heading-d">
                  Class Teacher
                </p>
                <div className="class-section">
                  {formMasterClasses.map((formMasterClass, index) => (
                    <React.Fragment key={index}>
                      <div className="subject-cards-d">
                        <div
                          className="subject-card"
                          onClick={() => saveScrollAndNavigate(
                            `/archives/${formMasterClass.sectionId}/${formMasterClass.sessionId}/${formMasterClass.termId}/${formMasterClass.id}`
                          )}
                        >
                          <p className="bold male-female-stat">
                            {formMasterClass.name} Full Result
                          </p>
                          <div className="teacher-list">
                            <p className="size5">
                              {formMasterClass.sessionName} - {formMasterClass.termName} Term
                            </p>
                          </div>
                        </div>
                        <div
                          className="subject-card"
                          onClick={() => saveScrollAndNavigate(
                            `/attendance/${formMasterClass.id}?section=${formMasterClass.sectionId}&session=${formMasterClass.sessionId}&term=${formMasterClass.termId}&sessionName=${encodeURIComponent(
                              formMasterClass.sessionName
                            )}&termName=${encodeURIComponent(formMasterClass.termName)}`
                          )}
                        >
                          <p className="bold male-female-stat">
                            {formMasterClass.name} Attendance
                          </p>
                          <div className="teacher-list">
                            <p className="size5">
                              {formMasterClass.sessionName} - {formMasterClass.termName} Term
                            </p>
                          </div>
                        </div>
                        <div
                          className="subject-card"
                          onClick={() => saveScrollAndNavigate(
                            `/psychomotor/${formMasterClass.id}?session=${formMasterClass.sessionId}&term=${formMasterClass.termId}&sessionName=${encodeURIComponent(
                              formMasterClass.sessionName
                            )}&termName=${encodeURIComponent(formMasterClass.termName)}`
                          )}
                        >
                          <p className="bold male-female-stat">
                            {formMasterClass.name} Psychomotor Skills
                          </p>
                          <span className="size5">
                            {formMasterClass.sessionName} - {formMasterClass.termName} Term
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

            <p className="size5 caps login-heading page-heading page-heading-d">
              My Assigned Subjects
            </p>
            {teacherSubjects.length > 0 ? (
         
              <div className="subject-list">
                {teacherSubjects.map((subject, index) => (
                  <div
                    key={index}
                    className="subject-card row gap-15"
                    onClick={() => saveScrollAndNavigate(
                      `/scores/${subject.classId}/${subject.subjectId}?session=${subject.sessionId}&term=${subject.termId}&sessionName=${encodeURIComponent(
                        subject.sessionName
                      )}&termName=${encodeURIComponent(subject.termName)}`
                    )}
                  >
                    <div>
                      <p className="bold male-female-stat">{subject.subjectName}</p>
                      <span className="size5">
                        {subject.className} ({subject.sectionName} -{" "}
                        {subject.sessionName} - {subject.termName} Term)
                      </span>
                    </div>
                    {subject.pdfLink && (
                      <button
                        className="list-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click from triggering
                          navigate(`/pdf/${encodeURIComponent(subject.pdfLink)}`);
                        }}
                      >
                        <i className="fas fa-file-pdf"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-subjects">No subjects assigned this term</p>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default CurrentClasses;